import { createMiddleware } from "@tanstack/react-start";
import { admit, type Need, type StaffRole, type UserRole } from "./portal";
import { assertOwnCall, readSessionFromCookie, requestPortal } from "./session";
import { runInUserContext } from "@/db/client";

export interface AuthContext {
  userId: string;
  email: string;
  role: UserRole;
}

/** Контекст сотрудника: роль сужена, подрядчика здесь не бывает. */
export interface StaffAuthContext extends AuthContext {
  role: StaffRole;
}

export const optionalAuth = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = await readSessionFromCookie();
  return next({ context: { auth } });
});

/**
 * Общая проверка: сессия → роль и портал по `admit` → транзакция с
 * `set local app.user_id`, которая даёт RLS-политикам (0003, 0018) правильный
 * контекст. См. src/db/client.ts: getDb() внутри обёрнутого хендлера вернёт
 * активную tx из ALS.
 */
async function gate(need: Need): Promise<AuthContext> {
  // Подделка запроса со страниц другого поддомена — до чтения сессии.
  assertOwnCall();
  const auth = await readSessionFromCookie();
  const verdict = admit(auth?.role, requestPortal(), need);
  if (!verdict.ok) {
    throw new Response(verdict.status === 401 ? "Unauthorized" : "Forbidden", {
      status: verdict.status,
    });
  }
  return auth!;
}

// requireAuth: любой СОТРУДНИК. До подрядчиков это значило «любой
// вошедший» — имя оставлено, чтобы не трогать полсотни функций, а смысл
// сужен: подрядчик сюда не проходит ни с какой кукой.
export const requireAuth = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = (await gate("staff")) as StaffAuthContext;
  return runInUserContext(auth.userId, () => next({ context: { auth, userId: auth.userId } }));
});

// requireAdmin: только администратор (создание юзеров и т.п.).
export const requireAdmin = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = (await gate("admin")) as StaffAuthContext;
  return runInUserContext(auth.userId, () => next({ context: { auth, userId: auth.userId } }));
});

// requireAdminOrRop: администратор и руководитель — справочники, жалобы,
// документы. Запись на уровне БД дополнительно защищена RLS.
export const requireAdminOrRop = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = (await gate("lead")) as StaffAuthContext;
  return runInUserContext(auth.userId, () => next({ context: { auth, userId: auth.userId } }));
});

// requireDocflow: документооборот — подрядчики, юрлица, сделки. Админ,
// руководитель и менеджер по документам; RLS (0023) говорит то же самое.
export const requireDocflow = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = (await gate("docflow")) as StaffAuthContext;
  return runInUserContext(auth.userId, () => next({ context: { auth, userId: auth.userId } }));
});

// requirePartner: только подрядчик и только на своём поддомене. RLS (0018)
// показывает ему одну строку — его собственную карточку.
export const requirePartner = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = await gate("partner");
  return runInUserContext(auth.userId, () => next({ context: { auth, userId: auth.userId } }));
});

// partnerSession: тот же подрядчик на своём поддомене, но БЕЗ транзакции на
// весь обработчик. Для долгих: распознавание документа идёт секунды на
// модели, отправка реквизитов ждёт DaData. Держать всё это время соединение
// пула значит дать десятку подрядчиков занять пул целиком (аудит 15.10,
// находка 1). Обработчик ходит в базу кусками — `runInUserContext(userId, …)`
// на каждое чтение и запись, — а сеть зовёт между ними.
export const partnerSession = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = await gate("partner");
  return next({ context: { auth, userId: auth.userId } });
});

// staffSession: любой сотрудник, но БЕЗ транзакции на весь обработчик — то же,
// что partnerSession для подрядчика. Для вопроса к регламентам: модель думает
// секунды, а справочной пользуются все сотрудники разом, и держать на это время
// соединение пула нельзя (аудит 15.10). Обработчик ходит в базу кусками
// `runInUserContext(userId, …)`.
export const staffSession = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = (await gate("staff")) as StaffAuthContext;
  return next({ context: { auth, userId: auth.userId } });
});
